import { useContext } from 'react';
import { Context } from '../../../App';

const fmt = (v, unit) => ((v || v === 0) && v !== '' ? `${Number(v)}${unit ? ` ${unit}` : ''}` : '—');

const fndTypeLabel = (t) => {
  if (t === 1 || t === '1') return 'Strip';
  if (t === 2 || t === '2') return 'Circular';
  if (t === 3 || t === '3') return 'Square';
  return '-';
};

// --- scale helpers (only Df & GWT) ---
const clamp = (x, a, b) => Math.min(b, Math.max(a, x));

export default function PureTerzaghiSketch({
  cohesion, // kPa
  phi, // °
  theta,
  df, // m
  fndWidth, // m (B)
  gamma, // kN/m^3
  fndType, // 0/1/2/3
  gwt, // m
  FsSt,
  FsSs, // unitless
  FSt,
  FSs,
  variant, // kPa
}) {
  const { theme: appTheme } = useContext(Context);

  const VW = 800;
  const VH = 350;

  const yGround = 20;
  const yFootTop = 20;

  const stemW = 50;
  const stemH = 110;

  const footingW = 250;
  const footingH = 40;

  const yFootBaseTop = yFootTop + stemH; // bottom of foundation (Df arrow must reach here)
  const yFootBaseBottom = yFootBaseTop + footingH; // true bottom of footing block
  const yBottom = VH - 5;

  const yfndTypeText = yGround + stemH + footingH / 2 + 5;
  const cx = VW / 2;

  // --- depth→pixel mapping (Df & GWT only) ---
  const soilPx = yBottom - yGround; // 315 px
  const Df = Number(df);
  const GWTin = gwt || gwt === 0 || gwt === '0' ? Number(gwt) : null;

  // *** FIX: keep foundation fixed; anchor scale so Df maps to actual footing bottom. ***
  // Anchor pixel position for Df (bottom of footing in this static drawing):
  const yDf_px = yFootBaseBottom;
  const anchorSpanPx = Math.max(1, yDf_px - yGround); // safety
  // Choose soilDepth_m so that: yDf_px = yGround + (Df / soilDepth_m) * soilPx
  const soilDepth_m = Df > 0 ? (Df * soilPx) / anchorSpanPx : 6; // fallback if df missing/zero
  const depthToY = (depth_m) => yGround + (depth_m / soilDepth_m) * soilPx;

  const hasGwt = GWTin !== null && !Number.isNaN(GWTin);
  let yGwt = hasGwt ? depthToY(GWTin) : null;

  // Snap exactly when GWT and Df are numerically equal (avoid hairline gaps from rounding/strokes)
  if (hasGwt && Math.abs(GWTin - Df) < 1e-6) yGwt = yDf_px;

  yGwt = hasGwt ? clamp(yGwt, yGround, yBottom) : null;
  const gwtClampedBottom = hasGwt ? GWTin > soilDepth_m : false;
  const gwtLabel = hasGwt ? `GWT = ${Number(GWTin)} m${gwtClampedBottom ? ' ↓ below view' : ''}` : 'GWT = —';

  const theme = variant === 'pdf-light' ? 'light' : appTheme;

  const concreteFillColor = theme === 'light' ? '#fff' : '#282828ff';
  const textBoxFillColor = theme === 'light' ? '#fff' : '#4c4c4cff';

  // --- Resultant arrow geometry (θ from vertical, up-right)
  const thetaNum = Number(theta ?? 0);
  const showThetaGlyph = thetaNum > 0;

  const th = Math.min(90, Math.max(0, thetaNum));
  const thRad = (th * Math.PI) / 180;

  const Px = cx; // origin at stem center
  const Py = yFootBaseTop; // top of footing (bottom of stem)

  // Arrow length & small text size
  const L = 90; // tweak if needed

  // Endpoint of R (up-right, since y decreases upward)
  const Rx = Px + L * Math.sin(thRad);
  const Ry = Py - L * Math.cos(thRad);

  return (
    <div class="sketch-rail">
      <div class="sketch-wrap">
        <svg
          viewBox={`0 0 ${VW} ${VH}`}
          preserveAspectRatio="xMidYMid meet"
          style={{
            display: 'block',
            width: '100%', // responsive width
            height: 'auto', // maintain aspect ratio
            maxWidth: '100%',
          }}
        >
          <defs>
            {/* smaller arrowheads */}
            <marker id="arrow-sm" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,1 L6,3 L0,5 Z" fill="currentColor" />
            </marker>
            <marker id="arrow-sm-down-blue" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,1 L6,3 L0,5 Z" fill="#1976d2" />
            </marker>
            <marker id="arrow-open-sm" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
              <path d="M0,0 L7,3.5 L0,7" fill="none" stroke="currentColor" strokeWidth="1.2" />
            </marker>
            <marker id="arrow-sm-up" markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
              <path d="M6,1 L0,3 L6,5 Z" fill="currentColor" />
            </marker>
            <marker id="arrow-sm-up-blue" markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
              <path d="M6,1 L0,3 L6,5 Z" fill="#1976d2" />
            </marker>
            <marker id="arrow-open-sm-right" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
              <path d="M0,0 L7,3.5 L0,7" fill="currentColor" />
            </marker>
            <marker id="arrow-open-sm-left" markerWidth="7" markerHeight="7" refX="2" refY="3.5" orient="auto">
              <path d="M7,0 L0,3.5 L7,7" fill="currentColor" />
            </marker>
            <marker id="arrow-sm-rev" markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
              <path d="M6,1 L0,3 L6,5 Z" fill="currentColor" />
            </marker>
            {/* Soil & concrete patterns */}
            <pattern id="soil-dots" patternUnits="userSpaceOnUse" width="15" height="15">
              <circle cx="2" cy="2" r="0.9" fill="currentColor" opacity="0.22" />
              <circle cx="6" cy="6" r="0.9" fill="currentColor" opacity="0.22" />
            </pattern>
            <pattern id="concreteFill" patternUnits="userSpaceOnUse" width="8" height="8">
              <rect width="8" height="8" fill="#d8d8d8ff" />
              <circle cx="2" cy="2" r="0.5" fill="#b0b0b0" />
              <circle cx="6" cy="4" r="0.5" fill="#a0a0a0" />
              <circle cx="4" cy="7" r="0.5" fill="#999" />
            </pattern>
            <pattern id="concreteHatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="6" stroke="#aaa" strokeWidth="0.5" />
            </pattern>
          </defs>

          <style>{`
          .sk-line   { stroke: currentColor; stroke-width: 1; }
          .sk-fill   { fill: none; stroke: currentColor; stroke-width: 1.6; }
          .sk-box    { fill: transparent; stroke: currentColor; stroke-opacity: 0.25; }
          .sk-text   { font-family: system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans"; fill: currentColor; }
          .sk-12     { font-size: 12px; }
          .sk-14     { font-size: 14px; }
          .sk-16     { font-size: 16px; font-weight: 600; }
          .sk-muted  { opacity: 0.9; }
          .sk-gwt    { stroke: #1976d2; stroke-width: 1.6; stroke-dasharray: 6 4; } /* MUI blue dashed */
          .sk-sat    { fill: #1976d2; opacity: 0.06; } /* light saturation tint below GWT */
          .sk-gwt-dimension    { stroke: #1976d2; stroke-width: 1; }
        `}</style>

          {/* Ground surface & frame */}
          <line className="sk-line" x1="20" y1={yGround} x2={VW - 20} y2={yGround} />
          <line className="sk-line" x1="20" y1={yBottom} x2={VW - 20} y2={yBottom} />
          <line className="sk-line" x1="20" y1={yGround} x2="20" y2={yBottom} />
          <line className="sk-line" x1={VW - 20} y1={yGround} x2={VW - 20} y2={yBottom} />
          <rect x="20" y={yGround} width={VW - 40} height={yBottom - yGround} fill="url(#soil-dots)" />

          <text className="sk-text sk-12 sk-muted" x={300} y={15} textAnchor="middle">
            Ground Surface
          </text>
          {/* Ground level symbol (standard 45° indicator) */}
          <g stroke="currentColor" strokeWidth="1.2" fill="none" transform={`translate(${-150}, ${0})`}>
            {/* Left 45° line */}
            <line x1={cx - 6} y1={yGround + 6} x2={cx} y2={yGround} />
            {/* Right 45° line */}
            <line x1={cx + 6} y1={yGround + 6} x2={cx} y2={yGround} />
            {/* Right 45° line */}
            <line x1={cx + 12} y1={yGround + 6} x2={cx + 6} y2={yGround} />
          </g>
          {/* Saturation tint below GWT (only visual) */}
          {hasGwt && <rect className="sk-sat" x="20" y={yGwt} width={VW - 40} height={Math.max(0, yBottom - yGwt)} />}

          {/* GWT symbol: apex of triangle exactly at GWT level */}
          {gwt && !gwtClampedBottom && (
            <g transform={`translate(${40}, ${yGwt})`} stroke="#1976d2" strokeWidth="1.4" fill="none">
              {/* main water line at GWT */}
              <line x1={-16} y1={0} x2={16} y2={0} />

              {/* inverted triangle with apex on the GWT line (0,0) */}
              <path d="M -7 -9 L 7 -9 L 0 0 Z" />

              {/* short horizontal lines below (graduations) */}
              <line x1={-8} y1={4} x2={8} y2={4} />
              <line x1={-6} y1={7} x2={6} y2={7} />
              <line x1={-4} y1={10} x2={4} y2={10} />
            </g>
          )}

          {/* Df arrow — from ground to BOTTOM of foundation (your existing arrow tip) */}
          {df && (
            <>
              <line
                className="sk-line"
                x1={cx - footingW / 2 - 80}
                y1={yGround}
                y2={yFootBaseTop + 38}
                x2={cx - footingW / 2 - 80}
                markerStart="url(#arrow-sm-up)"
                markerEnd="url(#arrow-sm)"
              />
              <line className="sk-line" x1={cx - footingW / 2 - 90} y1={170} x2={cx - footingW / 2 - 70} y2={170} />
              <text
                className="sk-text sk-14"
                x={cx - footingW / 2 - 86}
                y={(yGround + yFootBaseTop + 36) / 2}
                textAnchor="end"
                dominantBaseline="middle"
              >
                D<tspan baselineShift="sub">f</tspan>={fmt(df, 'm')}
              </text>
            </>
          )}

          {/* Dw arrow — BOTTOM of foundation to gwt */}
          {gwt > df && (
            <>
              <line
                className="sk-line"
                x1={cx - footingW / 2 - 80}
                y1={yFootBaseBottom}
                x2={cx - footingW / 2 - 80}
                y2={yGwt}
                markerStart="url(#arrow-sm-up)"
                markerEnd="url(#arrow-sm)"
              />
              <line className="sk-line" x1={cx - footingW / 2 - 90} y1={170} x2={cx - footingW / 2 - 70} y2={170} />
              <text
                className="sk-text sk-14"
                x={cx - footingW / 2 - 86}
                y={(yGwt + yFootBaseBottom) / 2}
                textAnchor="end"
                dominantBaseline="middle"
              >
                d<tspan baselineShift="sub">w</tspan>={fmt(gwt - df, 'm')}
              </text>
            </>
          )}

          {/* GWT (scaled to Df/GWT-only window) */}
          {hasGwt && (
            <>
              {/* gwt line and label */}
              <line className="sk-gwt" x1="20" y1={yGwt} x2={VW - 20} y2={yGwt} />
              <text className="sk-text sk-12 sk-muted" x="70" y={yGwt - 8 < yGround + 12 ? yGwt + 14 : yGwt - 8}>
                {gwtLabel}
              </text>
              {/* gwt dimension and label */}
              {gwt > 0 && (
                <line
                  className="sk-gwt-dimension"
                  x1={60}
                  y1={yGround}
                  y2={yGwt}
                  x2={60}
                  markerStart="url(#arrow-sm-up-blue)"
                  markerEnd="url(#arrow-sm-down-blue)"
                />
              )}
            </>
          )}
          {/* Footing stem and base */}
          {/* ------------------------------------------------------------------------------------ */}
          <rect x={cx - stemW / 2} y={yFootTop} width={stemW} height={stemH} fill={concreteFillColor} />
          <rect x={cx - stemW / 2} y={yFootTop} width={stemW} height={stemH} fill="url(#concreteHatch)" stroke="#666" />
          <rect x={cx - footingW / 2} y={yFootBaseTop} width={footingW} height={footingH} fill={concreteFillColor} />
          <rect
            x={cx - footingW / 2}
            y={yFootBaseTop}
            width={footingW}
            height={footingH}
            fill="url(#concreteHatch)"
            stroke="#666"
          />

          {fndWidth && (
            <>
              <line
                className="sk-line"
                x1={cx - footingW / 2}
                y1={yFootBaseTop + 50}
                x2={cx + footingW / 2}
                y2={yFootBaseTop + 50}
                markerStart="url(#arrow-open-sm-left)"
                markerEnd="url(#arrow-open-sm-right)"
              />
              <text className="sk-text sk-14" x={cx} y={yFootBaseTop + 65} textAnchor="middle">
                B={fmt(fndWidth, 'm')}
              </text>
            </>
          )}

          {/* Foundation type */}
          {fndType ? (
            <text className="sk-text sk-14" x={cx} y={yfndTypeText} textAnchor="middle">
              Foundation Type: {fndTypeLabel(fndType)}
            </text>
          ) : null}
          {(cohesion || phi || gamma) && (
            <g transform={`translate(${5}, ${20})`}>
              <rect
                /* Soil Properties Box*/
                x={VW - 260}
                y={17}
                width={220}
                height={95}
                rx="8"
                fill="currentColor"
                fillOpacity="0.05"
                stroke="currentColor"
                strokeWidth="0.5"
              />
              <rect x={VW - 260} y={17} width={220} height={95} rx="8" fill={textBoxFillColor} />
              <text className="sk-text sk-14" x={VW - 248} y={35} fontWeight="500">
                Soil Properties
              </text>
              {cohesion && (
                <>
                  <text className="sk-text sk-12" x={VW - 248} y={55}>
                    c = {fmt(cohesion, 'kPa')}
                  </text>
                </>
              )}
              {phi && (
                <>
                  <text className="sk-text sk-12" x={VW - 248} y={75}>
                    φ = {fmt(phi, '°')}
                  </text>
                </>
              )}
              {gamma && (
                <>
                  <text className="sk-text sk-12" x={VW - 248} y={95}>
                    γ = {fmt(gamma, 'kN/m')}
                    <tspan baselineShift="super">3</tspan>
                  </text>
                </>
              )}
            </g>
          )}

          {(FSt || FSs) && (
            <g transform={`translate(${5}, ${20})`}>
              <rect
                /* Loads Box*/
                x={VW - 260}
                y={120}
                width={220}
                height={50}
                rx="8"
                fill="currentColor"
                fillOpacity="0.05"
                stroke="currentColor"
                strokeWidth="0.5"
              />
              <rect x={VW - 260} y={120} width={220} height={50} rx="8" fill={textBoxFillColor} />
              <text className="sk-text sk-14" x={VW - 248} y={137} fontWeight="500">
                Loads
              </text>
              <text className="sk-text sk-12" x={VW - 248} y={155}>
                FSt = {(FSt || FSt === 0) && FSt !== '' ? `${Number(FSt)} kPa` : '—'}
              </text>
              <text className="sk-text sk-12" x={VW - 120} y={155}>
                FSs = {(FSs || FSs === 0) && FSs !== '' ? `${Number(FSs)} kPa` : '—'}
              </text>
              {/* Illustrative load arrows */}
              <g transform={`translate(${0}, ${-20})`}>
                <line
                  className="sk-line"
                  x1={cx - 60}
                  y1={yFootBaseBottom - 15}
                  x2={cx - 60}
                  y2={yFootBaseBottom}
                  markerEnd="url(#arrow-sm)"
                />
                <line
                  className="sk-line"
                  x1={cx}
                  y1={yFootBaseBottom - 15}
                  x2={cx}
                  y2={yFootBaseBottom}
                  markerEnd="url(#arrow-sm)"
                />
                <line
                  className="sk-line"
                  x1={cx + 60}
                  y1={yFootBaseBottom - 15}
                  x2={cx + 60}
                  y2={yFootBaseBottom}
                  markerEnd="url(#arrow-sm)"
                />
                <text className="sk-text sk-12 sk-muted" x={cx} y={yFootBaseBottom - 20} textAnchor="middle">
                  F (kPa)
                </text>
              </g>
            </g>
          )}

          {(FsSt || FsSs) && (
            <g transform={`translate(${5}, ${20})`}>
              <rect
                /* Factor of Safety Box*/
                x={VW - 260}
                y={178}
                width={220}
                height={50}
                rx="8"
                fill="currentColor"
                fillOpacity="0.05"
                stroke="currentColor"
                strokeWidth="0.5"
              />
              <rect x={VW - 260} y={178} width={220} height={50} rx="8" fill={textBoxFillColor} />
              <text className="sk-text sk-14" x={VW - 248} y={195} fontWeight="500">
                Factor of Safety
              </text>
              {FsSt && (
                <>
                  <text className="sk-text sk-12" x={VW - 248} y={215}>
                    FsSt = {FsSt ?? '—'}
                  </text>
                </>
              )}
              {FsSs && (
                <>
                  <text className="sk-text sk-12" x={VW - 120} y={215}>
                    FsSs = {FsSs ?? '—'}
                  </text>
                </>
              )}
            </g>
          )}
          {/* Placeholder overlay — show when inputs are empty */}
          {!fndType && !fndWidth && !df && !gwt && !cohesion && !phi && !gamma && !FsSt && !FsSs && !FSt && !FSs && (
            <>
              {/* soften the soil texture while empty */}
              <g opacity="0.3">
                <rect x="20" y={yGround} width={VW - 40} height={yBottom - yGround} fill="url(#soil-dots)" />
              </g>

              {/* centered message box */}
              <g>
                {/*
        Box size; tweak as needed
        w=360, h=72; centered in the SVG viewport
      */}
                <rect
                  x={(VW - 360) / 2}
                  y={VH / 2 + 50}
                  width={360}
                  height={72}
                  rx="10"
                  fill={concreteFillColor}
                  fillOpacity="1"
                  stroke="currentColor"
                  strokeOpacity=".7"
                />
                <text
                  x={VW / 2}
                  y={VH / 2 + 87}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="sk-text"
                  fontSize="14"
                  fill="currentColor"
                  opacity="0.7"
                >
                  Enter values to generate foundation diagram
                </text>
              </g>
            </>
          )}
          {showThetaGlyph && (
            <>
              {/* Vertical dashed axis inside the stem */}
              <line
                x1={cx}
                y1={yFootTop}
                x2={cx}
                y2={yFootBaseTop}
                stroke="currentColor"
                strokeWidth="1"
                strokeOpacity="0.45"
                strokeDasharray="5 4"
              />

              {/* Resultant R, same style as force arrows */}
              <line x1={Px} y1={Py} x2={Rx} y2={Ry} className="sk-line" markerStart="url(#arrow-sm-rev)" />

              {/* Label R near the arrow tip */}
              <text
                className="sk-text sk-12"
                x={Rx + 3 * Math.sin(thRad)}
                y={Ry - 3 * Math.cos(thRad)}
                textAnchor="start"
                dominantBaseline="central"
              >
                R
              </text>

              {/* θ indication between vertical axis and R */}
              {(() => {
                // Small arc centered at the stem-foot junction (Px, Py)
                const r = 20; // arc radius (px)
                const mx = Px;
                const my = Py;

                // Start point on the vertical-up ray (0, -1)
                const ax1x = mx;
                const ax1y = my - r;

                // End point on the R-direction unit vector (sinθ, -cosθ)
                const ax2x = mx + r * Math.sin(thRad);
                const ax2y = my - r * Math.cos(thRad);

                // SVG arc flags for a small, counter-clockwise arc
                const largeArc = 0;
                const sweep = 1;

                const arcPath = `M ${ax1x} ${ax1y} A ${r} ${r} 0 ${largeArc} ${sweep} ${ax2x} ${ax2y}`;
                const mid = thRad / 2;
                // NEW: label positioning logic
                let tx, ty, anchor;
                if (th < 15) {
                  // push θ to the LEFT of the dashed axis to avoid overlap with R
                  tx = mx - r + 18; // left of axis
                  ty = my - r * 0.85; // a bit above the junction
                  anchor = 'end';
                } else {
                  // normal mid-arc placement
                  tx = mx + (r + 6) * Math.sin(mid);
                  ty = my - (r + 6) * Math.cos(mid);
                  anchor = 'middle';
                }
                // Place θ text roughly at arc mid-angle, with a bit more offset if small

                // NEW: draw arc; add arrowhead only when small angle
                return (
                  <>
                    <path d={arcPath} className="sk-line" fill="none" />
                    <text className="sk-text sk-12" x={tx} y={ty} textAnchor={anchor} dominantBaseline="central">
                      θ
                    </text>
                  </>
                );
              })()}
            </>
          )}
        </svg>
      </div>
    </div>
  );
}
